import Component from '../../js/_Component';
import ResizeClasses from '../../js/util/_ResizeClasses';
import ScrollEnforcer from '../../js/util/_ScrollEnforcer';
import Carousel from '../Carousel/_Carousel';

/**
 * This is the structure of data returned through the Juicer JSON API.
 *
 * Implementation note: There are more fields than these available through the
 * Juicer API. If needed, they may be added to this interface, though to keep
 * things terse, only the most likely to be used ones have been added. Just hit
 * the API manually and take a look for what's available.
 */
interface JuicerData {
	name: string;
	last_synced: string;
	page_views_count: number;
	requested_at: string;
	posts: {
		current_page: number;
		limit_value: number;
		items: [{
			id: number;
			external_created_at: string;
			full_url: string;
			image: string;
			message: string;
			unformatted_message: string;
			likes: number;
			comments: number;
			source: {
				source: string;
			};
		}];
	};
}

/**
 * This is data explicitly needed when creating a card within the feed
 * component.
 */
interface CardInit {
	imageUrl: string;
	postUrl: string;
	message: string;
	source: string;
}

export default class JuicerFeed extends Component {
	/**
	 * The left carousel control.
	 */
	private leftButton: HTMLButtonElement;

	/**
	 * The right carousel control.
	 */
	private rightButton: HTMLButtonElement;

	/**
	 * The carousel component that manages the cards within the feed.
	 */
	private carousel: Carousel;

	constructor(element: HTMLElement) {
		super(element);

		new ResizeClasses(this.element, {
			// Small enough for all content content to break to one column.
			'm-narrow-content': {
				max: 768,
			},
			// Small enough for cards to break from two to one column.
			'm-narrow-card': {
				max: 1024,
			},
		});

		this.element.addEventListener('resizeclassesapplied', () => this.updateCarousel());

		this.leftButton = this.element.querySelector('.c-juicer-feed--control.m-left');
		this.rightButton = this.element.querySelector('.c-juicer-feed--control.m-right');

		const carouselElement = this.element.querySelector('.c-carousel') as HTMLElement;
		this.carousel = Component.getComponent(carouselElement);

		carouselElement.addEventListener('carouselitemchanged', () => this.updateControls());

		ScrollEnforcer.preventHorizontal(this.element);

		fetch('https://www.juicer.io/api/feeds/atxboats?page=1&per=10').then((response) => {
			response.json().then((data: JuicerData) => {
				data.posts.items.forEach((item) => {
					this.addCard({
						imageUrl: item.image,
						postUrl: item.full_url,
						message: item.unformatted_message,
						source: item.source.source.toLowerCase(),
					});
				});

				this.leftButton.addEventListener('click', () => this.carousel.prevItem());
				this.rightButton.addEventListener('click', () => this.carousel.nextItem());
				this.leftButton.disabled = true;
			});
		});

		this.updateCarousel();
	}

	/**
	 * Updates properties of the carousel based on this component's current state.
	 */
	updateCarousel() {
		if (this.element.classList.contains('m-narrow-card')) {
			this.carousel.setItemsDisplayed(1);
		} else {
			this.carousel.setItemsDisplayed(2);
		}
	}

	/**
	 * Updates controls based on the carousel's new state.
	 */
	updateControls() {
		this.leftButton.disabled = this.carousel.getCurrentItem() <= 0;
		this.rightButton.disabled = this.carousel.getCurrentItem()
			>= (this.carousel.getItemCount() - this.carousel.getItemsDisplayed());
	}

	/**
	 * Add a new card to the carousel.
	 * @param data The underlying data to add to the card.
	 */
	addCard(data: CardInit) {
		const template = this.element.querySelector(':scope > template') as HTMLTemplateElement;
		const newCard = document.importNode(template.content, true);

		const anchor = newCard.querySelector('.c-juicer-feed--photo') as HTMLAnchorElement;
		const message = newCard.querySelector('.c-juicer-feed--message') as HTMLParagraphElement;

		// Copied straight from the component to save time.
		const icons = {
			facebook: 'M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z',
			instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
			twitter: 'M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z',
			youtube: 'M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z',
		};

		anchor.href = data.postUrl;
		anchor.style.setProperty('background-image', `url('${data.imageUrl}')`);
		// Strip out excessive breaks and content after them.
		message.innerHTML = `${data.message.replace(/<br\b.*/, '<br>...')}<a class="c-juicer-feed--social-icon" href="${data.postUrl}" rel="noopener" target="_blank" aria-label="Go to post">
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
		<path d="${icons[data.source]}"/>
	</svg>
</a>`;

		this.carousel.addItem(newCard.children[0] as HTMLElement);
	}
}

Component.register(JuicerFeed, 'JuicerFeed');
